import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Package, Star, Share2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import RegisterField from "@/components/assets/RegisterField";

const RIGHTS = ["own_work", "cc_by", "cc0", "public_domain", "licensed_for_reuse"];
const err = (e, t) => toast.error(e?.response?.data?.detail || t("al.failed"));

export function UseInMission({ id, lang }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  useEffect(() => { if (open) api.get("/missions", { params: { target_language: lang } }).then((r) => setRows(r.data.missions)).catch(() => setRows([])); }, [open, lang]);
  const link = async (mid) => { try { await api.post(`/missions/${mid}/assets`, { asset_id: id, language: lang }); toast.success(t("al.p.linked")); } catch (e) { err(e, t); } };
  return (
    <div data-testid="use-in-mission">
      <button onClick={() => setOpen(!open)} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="uim-toggle"><Target className="w-4 h-4" /> {t("al.p.useInMission")}</button>
      {open && (
        <div className="nb-card p-3 mt-2 space-y-2 text-sm" data-testid="uim-panel">
          <Link to={`/teacher/library?asset=${id}&lang=${lang}`} className="nb-btn-primary py-1.5 px-3 text-sm inline-flex" data-testid="uim-new">{t("al.p.startMission")}</Link>
          <div className="text-xs text-gray-600">{t("al.p.orExisting")}</div>
          {rows?.length === 0 && <p className="text-xs text-gray-500" data-testid="uim-empty">{t("al.p.noMissions")}</p>}
          {(rows || []).slice(0, 8).map((m) => (
            <div key={m.mission_id} className="flex items-center justify-between gap-2"><span dir="auto">{m.title}</span>
              <button onClick={() => link(m.mission_id)} className="nb-tag bg-white text-xs" data-testid={`uim-link-${m.mission_id}`}>{t("al.p.attach")}</button></div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AddToPack({ id, collections, onAdded }) {
  const { t } = useI18n();
  const [cid, setCid] = useState("");
  const add = async () => { if (!cid) return; try { await api.post(`/library/collections/${cid}/items`, { kind: "asset", ref_id: id }); toast.success(t("al.p.addedToPack")); onAdded?.(); } catch (e) { err(e, t); } };
  if (!collections.length) return null;
  return (
    <div className="flex gap-1.5 items-center" data-testid="add-to-pack">
      <select className="nb-input w-auto py-1.5 text-sm" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="atp-select"><option value="">{t("al.p.choosePack")}</option>{collections.map((c) => <option key={c.collection_id} value={c.collection_id}>{c.name}</option>)}</select>
      <button onClick={add} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="atp-add"><Package className="w-4 h-4" /> {t("al.p.addToPack")}</button>
    </div>
  );
}

export function OwnActions({ a, orgId, onChanged }) {
  const { t } = useI18n();
  const [sug, setSug] = useState(null);
  const call = async (fn, msg) => { try { await fn(); toast.success(t(msg)); onChanged(); } catch (e) { err(e, t); } };
  return (
    <div className="nb-card bg-white p-3 space-y-2 text-sm" data-testid="own-actions">
      <div className="flex flex-wrap gap-2 items-center">
        <span className="nb-tag bg-butter text-xs" data-testid="own-status">{t(`al.p.st.${a.status}`)}</span>
        {a.status === "draft" && <button onClick={() => call(() => api.post(`/resources/assets/${a.asset_id}/ready`), "al.p.readyDone")} className="nb-btn-primary py-1.5 px-3 text-sm" data-testid="own-ready"><CheckCircle2 className="w-4 h-4" /> {t("al.p.markReady")}</button>}
        {orgId && <button onClick={() => call(() => api.post(`/resources/assets/${a.asset_id}/share`, { org_id: a.shared_org_id ? null : orgId }), a.shared_org_id ? "al.p.unshared" : "al.p.shared")} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="own-share"><Share2 className="w-4 h-4" /> {t(a.shared_org_id ? "al.p.unshare" : "al.p.share")}</button>}
        <button onClick={() => setSug(sug ? null : { rights: "", note: "" })} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="own-suggest"><Star className="w-4 h-4" /> {t("al.p.suggest")}</button>
      </div>
      {a.status === "draft" && <p className="text-xs text-gray-600">{t("al.p.draftPrivate")}</p>}
      <RegisterField a={a} editable={["draft", "teacher_ready"].includes(a.status)} onSaved={onChanged} />
      {sug && (
        <div className="space-y-2" data-testid="suggest-form">
          <p className="text-xs text-gray-600">{t("al.p.suggestNote")}</p>
          <select className="nb-input" value={sug.rights} onChange={(e) => setSug({ ...sug, rights: e.target.value })} data-testid="suggest-rights"><option value="">{t("al.p.rights")}</option>{RIGHTS.map((r) => <option key={r} value={r}>{t(`al.p.rt.${r}`)}</option>)}</select>
          <textarea className="nb-input" rows={2} dir="auto" maxLength={600} placeholder={t("al.p.suggestNotePh")} value={sug.note} onChange={(e) => setSug({ ...sug, note: e.target.value })} data-testid="suggest-note" />
          <button disabled={!sug.rights} onClick={() => call(() => api.post(`/resources/assets/${a.asset_id}/suggest`, sug), "al.p.suggested").then(() => setSug(null))} className="nb-btn-primary py-1.5 px-3 text-sm disabled:opacity-50" data-testid="suggest-send">{t("al.p.send")}</button>
        </div>
      )}
    </div>
  );
}
